import React, { useContext, useEffect, useState } from "react";
import { Container } from "@mui/material";
import SchemaComponent from "components/CBSchema";
import SEO from "components/CBSEO";
import CBLoading from "components/CBLoading";
import PageHeader from "components/CBPageHeader";
import SearchBarComponent from "components/CBSearchBar";
import ImprovedBlogResults from "./components/ImprovedBlogResults";

import { StyledMainPageContainer, StyledSectionBackgroundWrapper } from "index.styles";
import { constructSchemaData } from "utils/helpers/constructSchemaData";
import { SearchBar, SearchBarProvider } from "utils/context/searchBar.context";
import { fetchAllBlogs, filterBlogsBySearch, getImageUrl } from "utils/api/payloadCMS";
import { useParams } from "react-router-dom";
import ketamineTherapySeo from "./data/seoData/ketamineTherapySeo.data.json";
import psilocybinTherapySeo from "./data/seoData/psilocybinTherapySeo.data.json";
import onlineTherapySeo from "./data/seoData/onlineTherapySeo.data.json";
import defaultSeo from "./data/seoData/defaultSeo.data.json";
import { fetchBlogsByModality } from "utils/api/payloadCMS";

// Helper function to extract modality from blog slug
const extractModalityFromSlug = (slug) => {
  if (!slug) return null;

  const cleanSlug = slug.replace(/^\/+/, "");
  const pathParts = cleanSlug.split("/");

  // Check if first part is one of our modalities
  const validModalities = ["ketamine-therapy", "psilocybin-therapy", "online-therapy"];
  const possibleModality = pathParts[0];

  return validModalities.includes(possibleModality) ? possibleModality : null;
};

const blogHeaderDataMap = {
  "ketamine-therapy": {
    header: "The Ketamine Therapy & Mental Health Blog",
    headerTagline: "Discover and Learn More About the Power of Ketamine Therapy",
    headerAccent: ["Ketamine Therapy", "Mental Health"],
  },
  "psilocybin-therapy": {
    header: "The Psilocybin Therapy & Mental Health Blog",
    headerTagline: "Discover and Learn More About the Power of Psilocybin Therapy",
    headerAccent: ["Psilocybin Therapy", "Mental Health"],
  },
  "online-therapy": {
    header: "The Online Therapy & Mental Health Blog",
    headerTagline: "Discover and Learn More About the Power of Online Therapy",
    headerAccent: ["Online Therapy", "Mental Health"],
  },
};

const seoDataMap = {
  "ketamine-therapy": ketamineTherapySeo,
  "psilocybin-therapy": psilocybinTherapySeo,
  "online-therapy": onlineTherapySeo,
};

// Custom filter function that works with the SearchBar context
const filterPayloadBlogs = (blogs, combinedSearchTerms) => {
  if (!combinedSearchTerms || combinedSearchTerms.size === 0) {
    return blogs;
  }

  // Convert Set to string for our filter function
  const searchTerm = Array.from(combinedSearchTerms).join(" ");
  return filterBlogsBySearch(blogs, searchTerm);
};

// Helper function to extract article body from Payload CMS content
const extractArticleBody = (content) => {
  if (!content) return "";
  try {
    // Handle Payload CMS Lexical rich text structure
    if (typeof content === "string") return content;

    // For Lexical editor content, extract text from the JSON structure
    const extractTextFromLexical = (node) => {
      if (!node) return "";

      if (typeof node === "string") return node;

      if (Array.isArray(node)) {
        return node.map(extractTextFromLexical).join(" ");
      }

      if (typeof node === "object") {
        let text = "";

        // Handle different node types
        if (node.text) {
          text += node.text;
        }

        if (node.children) {
          text += extractTextFromLexical(node.children);
        }

        if (node.root && node.root.children) {
          text += extractTextFromLexical(node.root.children);
        }

        return text;
      }

      return "";
    };

    const extractedText = extractTextFromLexical(content);
    return extractedText.replace(/\s+/g, " ").trim();
  } catch (error) {
    return "";
  }
};

// Helper function to create multiple article schemas for blog listing
const createBlogArticleSchemas = (blogs, modalityType) => {
  return blogs.map((blog) => {
    const featuredImageUrl = getImageUrl(blog.featuredImage);
    const articleBody = extractArticleBody(blog.content);
    const wordCount = articleBody?.split(/\s+/).filter((word) => word.length > 0).length;
    const cleanSlug = blog.slug?.replace(/^\/+/, "");

    // NEW: Check if this specific blog has a modality in its slug
    const blogModality = extractModalityFromSlug(blog.slug);

    // Use blog-specific modality if found, otherwise fall back to URL modalityType
    const effectiveModality = blogModality || modalityType;

    const dynamicUrl = effectiveModality
      ? `https://www.almadelic.com/${effectiveModality}/blog/${cleanSlug.replace(`${effectiveModality}/blog/`, "")}`
      : `https://www.almadelic.com/blog/${cleanSlug}`;

    return {
      type: "Article",
      data: {
        headline: blog.title,
        authorName: blog.author?.name || "Almadelic",
        datePublished: blog.publishedDate || blog.createdAt,
        dateModified: blog.updatedAt || blog.publishedDate || blog.createdAt,
        image: featuredImageUrl,
        articleBody: articleBody,
        publisherName: "Almadelic Medical Group",
        publisherLogo: "https://d3a0rs3velfrz.cloudfront.net/logos/mainLogo.svg",
        description: blog.metaDescription || blog.description,
        mainEntityOfPage: dynamicUrl,
        wordCount: wordCount,
      },
    };
  });
};

// Enhanced constructSchemaData function for blog listing with multiple articles
const constructBlogListingSchemaData = (url, breadcrumbs, blogs, modalityType) => {
  // Get base schemas (Organization + BreadcrumbList for the listing page)
  const baseSchemas = constructSchemaData(url, breadcrumbs);

  // Add all blog article schemas with their individual breadcrumbs
  const blogSchemas = [];

  blogs.forEach((blog) => {
    const cleanSlug = blog.slug?.replace(/^\/+/, "");

    // Add Article schema
    const articleSchema = createBlogArticleSchemas([blog], modalityType)[0];
    blogSchemas.push(articleSchema);

    // NEW: Check if this specific blog has a modality in its slug
    const blogModality = extractModalityFromSlug(blog.slug);

    // Use blog-specific modality if found, otherwise fall back to URL modalityType
    const effectiveModality = blogModality || modalityType;

    // Add individual breadcrumb schema for each article
    const articleBreadcrumbs = [
      { name: "Home", url: "/" },
      { name: "Blog", url: "/blog" },
      { name: blog.title, url: `/blog/${cleanSlug}` },
    ];

    const formattedModality = effectiveModality
      ?.split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    const modalityBreadCrumbs = [
      { name: "Home", url: "/" },
      { name: `${formattedModality}`, url: `/${effectiveModality}` },
      { name: `${formattedModality} Blog`, url: `/${effectiveModality}/blog` },
      { name: blog.title, url: `/${cleanSlug}` },
    ];

    const dynamicBreadCrumbs = effectiveModality ? modalityBreadCrumbs : articleBreadcrumbs;

    blogSchemas.push({
      type: "BreadcrumbList",
      data: dynamicBreadCrumbs,
    });
  });

  // Combine all schemas
  return [...baseSchemas, ...blogSchemas];
};

const BlogContent = ({ modalityType }) => {
  const { filteredData } = useContext(SearchBar);
  const [loading, setLoading] = useState(true);
  const [initialBlogs, setInitialBlogs] = useState([]);
  const [debugInfo, setDebugInfo] = useState(null);

  // Load blogs from Payload CMS when component mounts
  useEffect(() => {
    const loadBlogs = async () => {
      setLoading(true);

      try {
        const blogData = modalityType ? await fetchBlogsByModality(modalityType) : await fetchAllBlogs();
        setInitialBlogs(blogData);
        setDebugInfo({
          source: "Payload CMS",
          count: blogData.length,
          apiUrl: process.env.REACT_APP_PAYLOAD_API_URL,
          environment: process.env.REACT_APP_ENV || "not set",
        });
      } catch (error) {
        setInitialBlogs([]);
        setDebugInfo({
          source: "Error",
          error: error.message,
        });
      } finally {
        setLoading(false);
      }
    };

    loadBlogs();
  }, [modalityType]);

  // Use filteredData when available (means search is active), otherwise use initialBlogs
  const blogsToDisplay = filteredData && filteredData.length >= 0 ? filteredData : initialBlogs;

  // Generate schema data for blog listing with multiple articles
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Blog", url: "/blog" },
  ];
  const formattedModality = modalityType
    ?.split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  const modalityBreadCrumbs = [
    { name: "Home", url: "/" },
    { name: `${formattedModality}`, url: `/${modalityType}` },
    { name: `${formattedModality} Blog`, url: `/${modalityType}/blog` },
  ];

  const dynamicBreadCrumbs = modalityType ? modalityBreadCrumbs : breadcrumbs;

  const dynamicUrl = `https://www.almadelic.com/${modalityType ? `${modalityType}/blog` : "blog"}`;
  const schemaData = constructBlogListingSchemaData(dynamicUrl, dynamicBreadCrumbs, blogsToDisplay, modalityType);

  return (
    <StyledMainPageContainer>
      <SchemaComponent schemas={schemaData} />
      {/* Seo data needs to be dynamic based on the modality */}
      <SEO
        title={seoDataMap[modalityType]?.title || defaultSeo.title}
        description={seoDataMap[modalityType]?.description || defaultSeo.description}
        canonicalUrl={seoDataMap[modalityType]?.canonicalUrl || defaultSeo.canonicalUrl}
      />

      <StyledSectionBackgroundWrapper>
        <PageHeader
          header={blogHeaderDataMap[modalityType]?.header || "The Psychedelic Therapy & Mental Health Blog"}
          headerTagline={blogHeaderDataMap[modalityType]?.headerTagline || "Discover and Learn More About the Power of Psychedelic Therapy"}
          headerAccent={blogHeaderDataMap[modalityType]?.headerAccent || ["Psychedelic Therapy", "Mental Health"]}
          lightText="true"
        />
      </StyledSectionBackgroundWrapper>

      <Container sx={{ marginTop: "2rem" }}>
        <SearchBarComponent placeholder="Search for blogs here..." color="#000" />
      </Container>

      {loading ? <CBLoading /> : <ImprovedBlogResults blogs={blogsToDisplay} />}
    </StyledMainPageContainer>
  );
};

const Blog = () => {
  // Load blogs first, then provide them to SearchBarProvider
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { modalityType } = useParams();

  useEffect(() => {
    const loadBlogs = async () => {
      try {
        const blogData = modalityType ? await fetchBlogsByModality(modalityType) : await fetchAllBlogs();
        setBlogs(blogData);
      } catch (error) {
        setBlogs([]);
      } finally {
        setLoading(false);
      }
    };

    loadBlogs();
  }, [modalityType]);

  if (loading) {
    return (
      <StyledMainPageContainer>
        <StyledSectionBackgroundWrapper>
          <PageHeader
            header={blogHeaderDataMap[modalityType]?.header || "The Psychedelic Therapy & Mental Health Blog"}
            headerTagline={blogHeaderDataMap[modalityType]?.headerTagline || "Discover and Learn More About the Power of Psychedelic Therapy"}
            headerAccent={blogHeaderDataMap[modalityType]?.headerAccent || ["Psychedelic Therapy", "Mental Health"]}
            lightText="true"
          />
        </StyledSectionBackgroundWrapper>
        <CBLoading />
      </StyledMainPageContainer>
    );
  }

  return (
    <SearchBarProvider initialData={blogs} filterFunction={filterPayloadBlogs}>
      <BlogContent modalityType={modalityType} />
    </SearchBarProvider>
  );
};

export default Blog;
